import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import getItemBulkBilling from 'client/actions/UtilityActivation/getItemBulkBilling';
import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrTable, { SR_TABLE_THEME_SMALL } from 'client/components/SrTable';
import SrText from 'client/components/SrText';
import SrBulkBillingStatusBadge from 'client/components/SrUtilityProfitAdminProcessActivationPage/SrBulkBillingStatusBadge';

const IN_FLIGHT_RETRIES = 5;
const IN_FLIGHT_RETRY_DELAY_MS = 400;

export const STATE_TO_LABEL_MAPPING = {
  paid: 'Paid',
  payment_failing: 'Payment failing',
  not_retrying: 'Payment failed, not retrying',
  canceled_by_stripe: 'Canceled by Stripe',
  canceled: 'Canceled',
  bill_manually: 'Bill manually in Stripe',
  awaiting_first_payment: 'Awaiting first payment',
  subscription_pending: 'Paid, subscription pending',
  needs_review: 'Needs review in Stripe',
};

const KIND_TO_LABEL_MAPPING = {
  initial: 'First month',
  recurring: 'Monthly',
  refund: 'Refund',
  chargeback: 'Chargeback',
  adjustment: 'Adjustment',
};

const SUBSCRIPTION_STATUS_TO_LABEL_MAPPING = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past due',
  unpaid: 'Unpaid',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Incomplete, expired',
  paused: 'Paused',
  pending: 'Pending',
};

export const subscriptionStatusLabel = (status) =>
  SUBSCRIPTION_STATUS_TO_LABEL_MAPPING[status] || status || '-';

export const showsNextRenewal = (subscription) =>
  !!subscription && !['canceled', 'incomplete_expired'].includes(subscription.status);

export const formatDate = (value) => (value ? moment.utc(value).format('MMM D, YYYY') : '-');

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '-');

export function bulkBillingSummary(billing) {
  if (!billing) {
    return null;
  }

  const lines = [];
  if (billing.failedAttempts > 0) {
    lines.push(
      `${billing.failedAttempts} failed ${billing.failedAttempts === 1 ? 'attempt' : 'attempts'} on the oldest unpaid invoice`,
    );
  }
  if (billing.disableRiskDate) {
    lines.push(
      `Could be disabled after ${moment(billing.disableRiskDate, 'YYYY-MM-DD').format('MMM D, YYYY')}`,
    );
  }

  return {
    label: STATE_TO_LABEL_MAPPING[billing.state] || billing.state,
    warning: billing.warning,
    lines,
  };
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SrBulkBillingPanel = ({ itemId }) => {
  const dispatch = useDispatch();
  const [billing, setBilling] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    setBilling(null);
    setLoadFailed(false);

    const load = async () => {
      for (let attempt = 0; attempt <= IN_FLIGHT_RETRIES && isCurrent; attempt += 1) {
        const result = await dispatch(getItemBulkBilling, {
          itemId,
          onError: () => {
            if (isCurrent) {
              setLoadFailed(true);
            }
          },
        });
        if (result) {
          if (isCurrent) {
            setBilling(result.billing);
          }
          return;
        }
        if (result === null) {
          return;
        }
        if (attempt < IN_FLIGHT_RETRIES) {
          await wait(IN_FLIGHT_RETRY_DELAY_MS);
        }
      }
      if (isCurrent) {
        setLoadFailed(true);
      }
    };
    load();

    return () => {
      isCurrent = false;
    };
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadFailed) {
    return (
      <SrBox marginTop="10px" paddingBottom="16px">
        <SrText font="greycliff" size="14" color="grey">
          Bulk billing status could not be loaded
        </SrText>
      </SrBox>
    );
  }

  const summary = bulkBillingSummary(billing);
  if (!summary) {
    return null;
  }

  const { subscription, attempts } = billing;

  return (
    <SrBox marginTop="10px" paddingBottom="16px">
      <SrBox marginBottom="8px">
        <SrText font="greycliff" size="16" weight="800">
          Bulk billing
        </SrText>
      </SrBox>
      <SrFlex columnGap="8px" align="center">
        <SrBulkBillingStatusBadge
          state={billing.state}
          label={summary.label}
          warning={summary.warning}
        />
      </SrFlex>
      {summary.lines.map((line) => (
        <SrBox key={line} marginTop="6px">
          <SrText
            font="greycliff"
            size="14"
            weight="600"
            color={summary.warning ? 'orange300' : 'grey'}
          >
            {line}
          </SrText>
        </SrBox>
      ))}
      {subscription && (
        <SrBox marginTop="14px">
          <SrBox marginBottom="6px">
            <SrText font="greycliff" size="14" weight="800">
              Subscription
            </SrText>
          </SrBox>
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: '4px' }}>
            <SrText font="greycliff" size="14" weight="700">
              Status
            </SrText>
            <SrText font="greycliff" size="14">
              {subscriptionStatusLabel(subscription.status)}
            </SrText>
            <SrText font="greycliff" size="14" weight="700">
              Current period
            </SrText>
            <SrText font="greycliff" size="14">
              {`${formatDate(subscription.currentPeriodStart)} - ${formatDate(subscription.currentPeriodEnd)}`}
            </SrText>
            {showsNextRenewal(subscription) && (
              <>
                <SrText font="greycliff" size="14" weight="700">
                  Next renewal
                </SrText>
                <SrText font="greycliff" size="14">
                  {formatDate(subscription.currentPeriodEnd)}
                </SrText>
              </>
            )}
          </div>
        </SrBox>
      )}
      <SrBox marginTop="14px">
        <SrBox marginBottom="6px">
          <SrText font="greycliff" size="14" weight="800">
            Payment attempts
          </SrText>
        </SrBox>
        {attempts.length === 0 ? (
          <SrText font="greycliff" size="14" color="grey">
            No payment attempts yet
          </SrText>
        ) : (
          <SrTable
            theme={SR_TABLE_THEME_SMALL}
            noPadding
            headers={['Kind', 'Amount', 'Outcome', 'Failure code', 'Date'].map((title) => ({
              content: (
                <SrText font="greycliff" size="14" weight="700">
                  {title}
                </SrText>
              ),
            }))}
            rows={attempts
              .slice()
              .sort((a, b) => moment(b.attemptedAt).valueOf() - moment(a.attemptedAt).valueOf())
              .map((attempt) => ({
                cells: [
                  { content: KIND_TO_LABEL_MAPPING[attempt.kind] || attempt.kind },
                  { content: `$${Number(attempt.amount).toFixed(2)}` },
                  {
                    content:
                      attempt.status === 'failed' ? (
                        <SrText color="orange300">{capitalize(attempt.status)}</SrText>
                      ) : (
                        capitalize(attempt.status)
                      ),
                  },
                  { content: attempt.failureCode || '-' },
                  { content: formatDate(attempt.attemptedAt) },
                ],
              }))}
          />
        )}
      </SrBox>
    </SrBox>
  );
};

SrBulkBillingPanel.propTypes = {
  itemId: PropTypes.number.isRequired,
};

SrBulkBillingPanel.displayName = 'SrBulkBillingPanel';

export default SrBulkBillingPanel;
